import { Hash } from 'lucide-react'
import { useMemo } from 'react'
import type { ComboboxOption } from '@/components/combobox'
import { useLocationOptions } from '@/features/locations/api'
import { usePermission } from '@/hooks/use-permission'
import { timeZoneLabel } from '@/lib/timezones'
import { useGroups, useTags } from '../api'

export function Swatch({ color, className = 'size-2.5' }: { color: string | null | undefined; className?: string }) {
  return <span className={`${className} shrink-0 rounded-full bg-muted-foreground/40`} style={color ? { backgroundColor: color } : undefined} aria-hidden />
}

export function useGroupOptions(): ComboboxOption[] {
  const enabled = usePermission('displays.view')
  const groups = useGroups(enabled)
  return useMemo(
    () => (groups.data ?? []).map((g) => ({ value: g.id, label: g.name, prefix: <Swatch color={g.color} />, hint: String(g.displays_count) })),
    [groups.data],
  )
}

export function useTagOptions(): ComboboxOption[] {
  const enabled = usePermission('displays.view')
  const tags = useTags(enabled)
  return useMemo(
    () => (tags.data ?? []).map((t) => ({ value: t.id, label: t.name, prefix: <Hash className="size-3 shrink-0" style={{ color: t.color ?? undefined }} />, keywords: [t.slug] })),
    [tags.data],
  )
}

export function useLocationComboOptions(): ComboboxOption[] {
  const enabled = usePermission('displays.view')
  const locations = useLocationOptions(enabled)
  return useMemo(
    () => (locations.data ?? []).map((l) => ({ value: l.id, label: l.name, hint: timeZoneLabel(l.timezone), keywords: [l.city ?? ''] })),
    [locations.data],
  )
}

/** ~10 preset swatches for groups and tags (stored as #RRGGBB). */
export const PRESET_COLORS = ['#7c3aed', '#4f46e5', '#0ea5e9', '#06b6d4', '#10b981', '#84cc16', '#f59e0b', '#f97316', '#ef4444', '#ec4899', '#64748b'] as const
